import React, { useState } from "react";
import PropTypes from "prop-types";
import Checkbox from "../../inputs/checkbox";
import CriterionSummary from "./CriterionSummary";
import * as CONST from "../../../utilities/constants";
// The PrimeReact accordion rules this card's markup renders. Imported here since
// ASB-2995: its previous importer, components/accordion/AccordionItem.js, was the
// modeled page's and was deleted with it, which would otherwise drop this stylesheet
// out of the bundle entirely.
import "../../accordion.scss";
import "./TechnographicsCard.scss";

/**
 * TechnographicsCard - Criteria card for technographic targeting
 *
 * Data format: {
 *   os: { windows: true, macos: false, ... },
 *   browser: { chrome: true, safari: false, ... },
 *   deviceType: { desktop: true, mobile: false, ... }
 * }
 */
function TechnographicsCard({ data, onChange, isReadOnly, isCollapsed }) {
	// Track which accordion sections are open
	const [openSections, setOpenSections] = useState([]);

	// Toggle accordion section
	const toggleSection = (e, sectionId) => {
		e.preventDefault();
		if (openSections.includes(sectionId)) {
			setOpenSections((prev) => prev.filter((s) => s !== sectionId));
		} else {
			setOpenSections((prev) => [...prev, sectionId]);
		}
	};

	// Handle checkbox change
	const handleCheckboxChange = (e, category) => {
		const key = e.target.value;
		const checked = e.target.checked;

		const newData = { ...data };
		if (!newData[category]) {
			newData[category] = {};
		}
		newData[category] = { ...newData[category], [key]: checked };
		onChange(newData);
	};

	// Get selected values for Checkbox component format
	const getSelectedForCheckbox = (category, options) => {
		if (!data?.[category]) return null;
		return Object.entries(data[category])
			.filter(([key, value]) => value === true)
			.map(([key]) => {
				const opt = options.find((o) => o.value === key);
				return opt ? { label: opt.label, value: key } : null;
			})
			.filter(Boolean);
	};

	// Collapsed view - show selected items as chips
	if (isCollapsed) {
		return (
			<div className="technographics-card collapsed">
				<CriterionSummary type="technographics" data={data} />
			</div>
		);
	}

	// Expanded view - accordion style matching existing Technographics
	return (
		<div className="technographics-card expanded">
			<div className="p-accordion p-component">
				{/* Operating Systems */}
				<div
					className={`p-accordion-tab${openSections.includes("os") ? " p-accordion-tab-active" : ""}`}
				>
					<div className={`p-accordion-header${openSections.includes("os") ? " p-highlight" : ""}`}>
						<a
							href="#"
							className="p-accordion-header-link"
							role="tab"
							tabIndex="0"
							onClick={(e) => toggleSection(e, "os")}
						>
							<svg
								width="14"
								height="14"
								viewBox="0 0 14 14"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
								className="p-icon p-accordion-toggle-icon"
							>
								<path
									d="M7.01744 10.398C6.91269 10.3985 6.8089 10.378 6.71215 10.3379C6.61541 10.2977 6.52766 10.2386 6.45405 10.1641L1.13907 4.84913C1.03306 4.69404 0.985221 4.5065 1.00399 4.31958C1.02276 4.13266 1.10693 3.95838 1.24166 3.82747C1.37639 3.69655 1.55301 3.61742 1.74039 3.60402C1.92777 3.59062 2.11386 3.64382 2.26584 3.75424L7.01744 8.47394L11.769 3.75424C11.9189 3.65709 12.097 3.61306 12.2748 3.62921C12.4527 3.64535 12.6199 3.72073 12.7498 3.84328C12.8797 3.96582 12.9647 4.12842 12.9912 4.30502C13.0177 4.48162 12.9841 4.662 12.8958 4.81724L7.58083 10.1322C7.50996 10.2125 7.42344 10.2775 7.32656 10.3232C7.22968 10.3689 7.12449 10.3944 7.01744 10.398Z"
									fill="currentColor"
								></path>
							</svg>
							<span className="p-accordion-header-text">Operating Systems</span>
						</a>
					</div>
					<div className="p-toggleable-content">
						<div className="p-accordion-content">
							<Checkbox
								name="os"
								customClasses="technographics-boxes"
								required={false}
								disabled={isReadOnly}
								style="list"
								selected={getSelectedForCheckbox("os", CONST.os)}
								onInput={(e) => handleCheckboxChange(e, "os")}
								options={CONST.os}
							/>
						</div>
					</div>
				</div>

				{/* Browser */}
				<div
					className={`p-accordion-tab${openSections.includes("browser") ? " p-accordion-tab-active" : ""}`}
				>
					<div className={`p-accordion-header${openSections.includes("browser") ? " p-highlight" : ""}`}>
						<a
							href="#"
							className="p-accordion-header-link"
							role="tab"
							tabIndex="0"
							onClick={(e) => toggleSection(e, "browser")}
						>
							<svg
								width="14"
								height="14"
								viewBox="0 0 14 14"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
								className="p-icon p-accordion-toggle-icon"
							>
								<path
									d="M7.01744 10.398C6.91269 10.3985 6.8089 10.378 6.71215 10.3379C6.61541 10.2977 6.52766 10.2386 6.45405 10.1641L1.13907 4.84913C1.03306 4.69404 0.985221 4.5065 1.00399 4.31958C1.02276 4.13266 1.10693 3.95838 1.24166 3.82747C1.37639 3.69655 1.55301 3.61742 1.74039 3.60402C1.92777 3.59062 2.11386 3.64382 2.26584 3.75424L7.01744 8.47394L11.769 3.75424C11.9189 3.65709 12.097 3.61306 12.2748 3.62921C12.4527 3.64535 12.6199 3.72073 12.7498 3.84328C12.8797 3.96582 12.9647 4.12842 12.9912 4.30502C13.0177 4.48162 12.9841 4.662 12.8958 4.81724L7.58083 10.1322C7.50996 10.2125 7.42344 10.2775 7.32656 10.3232C7.22968 10.3689 7.12449 10.3944 7.01744 10.398Z"
									fill="currentColor"
								></path>
							</svg>
							<span className="p-accordion-header-text">Browser</span>
						</a>
					</div>
					<div className="p-toggleable-content">
						<div className="p-accordion-content">
							<Checkbox
								name="browser"
								customClasses="technographics-boxes"
								required={false}
								disabled={isReadOnly}
								style="list"
								selected={getSelectedForCheckbox("browser", CONST.browser)}
								onInput={(e) => handleCheckboxChange(e, "browser")}
								options={CONST.browser}
							/>
						</div>
					</div>
				</div>

				{/* Device Type */}
				<div
					className={`p-accordion-tab${openSections.includes("deviceType") ? " p-accordion-tab-active" : ""}`}
				>
					<div className={`p-accordion-header${openSections.includes("deviceType") ? " p-highlight" : ""}`}>
						<a
							href="#"
							className="p-accordion-header-link"
							role="tab"
							tabIndex="0"
							onClick={(e) => toggleSection(e, "deviceType")}
						>
							<svg
								width="14"
								height="14"
								viewBox="0 0 14 14"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
								className="p-icon p-accordion-toggle-icon"
							>
								<path
									d="M7.01744 10.398C6.91269 10.3985 6.8089 10.378 6.71215 10.3379C6.61541 10.2977 6.52766 10.2386 6.45405 10.1641L1.13907 4.84913C1.03306 4.69404 0.985221 4.5065 1.00399 4.31958C1.02276 4.13266 1.10693 3.95838 1.24166 3.82747C1.37639 3.69655 1.55301 3.61742 1.74039 3.60402C1.92777 3.59062 2.11386 3.64382 2.26584 3.75424L7.01744 8.47394L11.769 3.75424C11.9189 3.65709 12.097 3.61306 12.2748 3.62921C12.4527 3.64535 12.6199 3.72073 12.7498 3.84328C12.8797 3.96582 12.9647 4.12842 12.9912 4.30502C13.0177 4.48162 12.9841 4.662 12.8958 4.81724L7.58083 10.1322C7.50996 10.2125 7.42344 10.2775 7.32656 10.3232C7.22968 10.3689 7.12449 10.3944 7.01744 10.398Z"
									fill="currentColor"
								></path>
							</svg>
							<span className="p-accordion-header-text">Device Type</span>
						</a>
					</div>
					<div className="p-toggleable-content">
						<div className="p-accordion-content">
							<Checkbox
								name="deviceType"
								customClasses="technographics-boxes"
								required={false}
								disabled={isReadOnly}
								style="list"
								selected={getSelectedForCheckbox("deviceType", CONST.device)}
								onInput={(e) => handleCheckboxChange(e, "deviceType")}
								options={CONST.device}
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

TechnographicsCard.propTypes = {
	data: PropTypes.shape({
		os: PropTypes.object,
		browser: PropTypes.object,
		deviceType: PropTypes.object,
	}),
	onChange: PropTypes.func.isRequired,
	isReadOnly: PropTypes.bool,
	isCollapsed: PropTypes.bool,
};

TechnographicsCard.defaultProps = {
	data: {},
	isReadOnly: false,
	isCollapsed: false,
};

// Export helper for getting selected count (used in header)
TechnographicsCard.getSelectedCount = (data) => {
	let count = 0;
	if (data?.os) count += Object.values(data.os).filter(Boolean).length;
	if (data?.browser) count += Object.values(data.browser).filter(Boolean).length;
	if (data?.deviceType) count += Object.values(data.deviceType).filter(Boolean).length;
	return count;
};

export default TechnographicsCard;
